import { isArray, isBoolean, isNaN } from 'lodash';
import * as yup from 'yup';
import { validateText, validateObject } from 'utils/validation';
import { Venue } from 'store/venues';
import { Kiosk } from 'store/kiosks';
import { Area } from 'store/areas';
import { ClientIntegration } from 'store/clientIntegrations';
import { Configuration } from 'store/configurations';
import { TicketTemplate } from 'store/ticketTemplates';
import { Integration } from 'store/integrations';
import { KioskHardware } from 'store/kioskHardware';
import { KioskTicketTemplate } from 'store/kioskTicketTemplates';
import { Hardware } from 'store/hardware';
import { PropertyType } from './types';
import { INVALID } from './constants'; // -- needs to be changed
import { formatTitle } from 'utils/stringFormat';

export const areaValidationSchema = yup.object().shape({
  name: validateText(),
  description: validateText()
});

export const ticketTemplateValidationSchema = yup.object().shape({
  template: validateText()
});

export const clientIntegrationValidationSchema = yup.object().shape({
  name: validateText(),
  baseUrl: validateText()
});

export const configValidationSchema = yup.object().shape({
  name: validateText()
});

export const kioskValidationSchema = yup.object().shape({
  name: validateText(),
  macAddress: validateText()
});

export const venueValidationSchema = yup.object().shape({
  name: validateText(),
  address: validateText()
});

export const integrationValidationSchema = yup.object().shape({
  name: validateText(),
  code: validateText()
});

export const hardwareValidationSchema = yup.object().shape({
  name: validateText(),
  code: validateText()
});

export const kioskHardwareValidationSchema = yup.object().shape({
  serialCode: validateText(),
  configuration: validateObject(),
  values: validateObject()
});

export const customerValidationSchema = yup.object().shape({
  request: validateObject()
});

export const getVenueInitialForm = (venue?: Venue) => ({
  name: venue ? venue.name : '',
  address: venue ? venue.address : ''
});

export const getAreaInitialForm = (area?: Area) => ({
  name: area ? area.name : '',
  description: area ? area.description : '',
  venueId: area ? area.venueId : ''
});

export const getKioskInitialForm = (kiosk?: Kiosk) => ({
  name: kiosk ? kiosk.name : '',
  configurationId: kiosk ? kiosk.configurationId : '',
  clientIntegrationId: kiosk ? kiosk.clientIntegrationId : '',
  areaId: kiosk ? kiosk.areaId : '',
  macAddress: kiosk ? kiosk.macAddress : ''
});

export const getKioskHardwareInitialForm = (kioskHardware?: KioskHardware) => ({
  kioskId: kioskHardware ? kioskHardware.kioskId : '',
  hardwareId: kioskHardware ? kioskHardware.hardwareId : '',
  serialCode: kioskHardware ? kioskHardware.serialCode : '',
  configuration: kioskHardware ? JSON.stringify(kioskHardware.configuration) : '',
  values: kioskHardware ? JSON.stringify(kioskHardware.values) : '',
  status: kioskHardware ? kioskHardware.status : ''
});

export const getKioskTicketTemplateInitialForm = (kioskTicketTemplate?: KioskTicketTemplate) => ({
  kioskId: kioskTicketTemplate ? kioskTicketTemplate.kioskId : '',
  ticketTemplateId: kioskTicketTemplate ? kioskTicketTemplate.ticketTemplateId : ''
});

export const getHardwareInitialForm = (hardware?: Hardware) => ({
  name: hardware ? hardware.name : '',
  type: hardware ? hardware.type : '',
  code: hardware ? hardware.code : ''
});

export const getCIInitialForm = (clientIntegration?: ClientIntegration) => ({
  name: clientIntegration ? clientIntegration.name : '',
  integrationId: clientIntegration ? clientIntegration.integration.id : '',
  baseUrl: clientIntegration ? clientIntegration.baseUrl : ''
});

export const getIntegrationInitialForm = (integration?: Integration) => ({
  name: integration ? integration.name : '',
  code: integration ? integration.code : ''
});

export const getConfigurationInitialForm = (configuration?: Configuration) => ({
  name: configuration ? configuration.name : ''
});

export const getTicketTemplateInitialForm = (ticketTemplate?: TicketTemplate) => ({
  type: ticketTemplate ? ticketTemplate.type : '',
  template: ticketTemplate ? ticketTemplate.template : ''
});

export const getCustomerInitialForm = () => ({
  request: ''
});
/**
 * Checks to see if any of the form values is empty.
 * @param {object} form - form object --> has the shape of getInitialForm returned object
 */
export const getHasEmptyInput = (
  form: ReturnType<
    | typeof getVenueInitialForm
    | typeof getKioskInitialForm
    | typeof getCIInitialForm
    | typeof getConfigurationInitialForm
    | typeof getHardwareInitialForm
    | typeof getKioskHardwareInitialForm
    | typeof getTicketTemplateInitialForm
    | typeof getAreaInitialForm
    | typeof getIntegrationInitialForm
    | typeof getCustomerInitialForm
  >
) => {
  return Object.values(form).some((value) => value === '');
};

export const getHasEmpty = (values: (string | null | undefined)[]) => {
  return values.some((value) => !value || !value.toString().trim().length);
};

/**
 * Checks to see if any of validation object values has INVALID
 * @param {object} validation - validation object --> has the shape of validationSchema object
 */
export const getHasInvalidInput = (validation: { [x: string]: string }) => {
  const tempValidation = validation;
  return Object.values(tempValidation).some((value) => value === INVALID);
};

export const TemplateTypeList = [
  { name: 'sale', code: 'sale' },
  { name: 'receipt', code: 'receipt' },
  { name: 'test', code: 'test' },
  { name: 'eticket', code: 'eticket' }
];

export const HardwareTypeList = [
  { name: 'printer', code: 'printer' },
  { name: 'payment device', code: 'paymentDevice' },
  { name: 'barcode scanner', code: 'barcodeScanner' }
];

export const integrationColumns = [
  { key: 'name', type: 'string' },
  { key: 'code', type: 'string' }
];

export const venueColumns = [
  { key: 'name', type: 'string' },
  { key: 'address', type: 'string' }
];

export const ticketTemplateColumns = [
  { key: 'type', type: 'templateType' },
  { key: 'template', type: 'string' }
];

export const areaColumns = [
  { key: 'name', type: 'string' },
  { key: 'description', type: 'string' },
  { key: 'venue', type: 'venue' }
];

export const clientIntegrationColumns = [
  { key: 'name', type: 'string' },
  { key: 'baseUrl', type: 'string' },
  { key: 'integration', type: 'integration' },
  {
    key: 'configuration',
    type: 'object',
    children: [
      // COMMON
      { key: 'timezone', type: 'string' },
      { key: 'apiKey', type: 'string' },
      { key: 'numberOfDaysToSell', type: 'number' },
      { key: 'numberOfDaysToSellForKCC', type: 'number' },
      { key: 'printerAssignments', type: 'array' },
      // TESSITTURA
      { key: 'username', type: 'string' },
      { key: 'password', type: 'string' },
      { key: 'constituentId', type: 'number' },
      { key: 'paymentTypes', type: 'object' },
      {
        key: 'modeOfSaleId',
        type: 'object',
        children: [
          { key: 'default', type: 'number' },
          { key: 'member', type: 'number' }
        ]
      },
      { key: 'sourceId', type: 'number' },
      { key: 'deliveryMethodId', type: 'number' },
      {
        key: 'willcall',
        type: 'object',
        children: [
          { key: 'validStatuses', type: 'array' },
          { key: 'printedStatuses', type: 'array' }
        ]
      },
      { key: 'facilityIds', type: 'array' },
      { key: 'version', type: 'string' },
      { key: 'rankMap', type: 'object' },
      { key: 'zoneExpiration', type: 'number' },
      { key: 'sortTicketTypes', type: 'array' },
      { key: 'lookupOrdersEndDate', type: 'number' },
      { key: 'orderNotesTemplate', type: 'array' },
      { key: 'countryToStateMap', type: 'object' },
      { key: 'priceTypeLabel', type: 'string' },
      { key: 'zoneLabel', type: 'string' },
      {
        key: 'entitlements',
        type: 'object',
        children: [
          { key: 'discoveryStoredProcedureName', type: 'string' },
          { key: 'validationStoredProcedureName', type: 'string' }
        ]
      },
      { key: 'sellPackages', type: 'boolean' },
      { key: 'packageLabel', type: 'string' },
      { key: 'useCompositeTickets', type: 'boolean' },
      { key: 'eventsToSell', type: 'array' },
      { key: 'constituentSearchAttribute', type: 'string' },
      { key: 'constituentTypesForSearch', type: 'array' },
      {
        key: 'createConstituentParams',
        type: 'object',
        children: [
          { key: 'ConstituentType', type: 'object' },
          { key: 'FirstName', type: 'string' },
          { key: 'LastName', type: 'string' },
          { key: 'MailIndicator', type: 'object' },
          { key: 'EmarketIndicator', type: 'object' },
          { key: 'MiddleName', type: 'string' },
          { key: 'NameStatus', type: 'object' },
          { key: 'OriginalSource', type: 'object' },
          { key: 'PhoneIndicator', type: 'object' },
          { key: 'Prefix', type: 'object' },
          { key: 'Gender', type: 'object' },
          { key: 'Suffix', type: 'object' },
          { key: 'Salutation', type: 'object' },
          { key: 'Address', type: 'object' },
          { key: 'PrimaryAddress', type: 'object' },
          { key: 'ElectronicAddress', type: 'object' },
          { key: 'PrimaryElectronicAddress', type: 'object' },
          { key: 'PrimaryPhoneNumbers', type: 'object' },
          { key: 'PrimaryPhone', type: 'object' },
          { key: 'SortName', type: 'string' },
          { key: 'ProtectionType', type: 'object' }
        ]
      },
      { key: 'newConstituentAttributes', type: 'array' },
      {
        key: 'emailDelivery',
        type: 'object',
        children: [
          { key: 'mobileTicket', type: 'object' },
          { key: 'orderConfirmation', type: 'object' }
        ]
      },
      {
        key: 'printHtmlTickets',
        type: 'object',
        children: [
          { key: 'headerDesignId', type: 'number' },
          { key: 'ticketDesignId', type: 'number' },
          { key: 'includeReceipts', type: 'boolean' },
          { key: 'templateId', type: 'number' },
          { key: 'includeWalletTicketUrl', type: 'boolean' }
        ]
      },
      {
        key: 'printTicketElements',
        type: 'object',
        children: [
          { key: 'ticketDesignId', type: 'number' },
          { key: 'includeReceipts', type: 'boolean' }
        ]
      },
      {
        key: 'frostCustomPackageDescriptionLookup',
        type: 'boolean'
      },
      {
        key: 'cwfCustomOrderSearch',
        type: 'object',
        children: [
          { key: 'absoluteUrl', type: 'string' },
          { key: 'headers', type: 'object' }
        ]
      },
      {
        key: 'membership',
        type: 'object',
        children: [
          { key: 'organizationId', type: 'number' },
          { key: 'levelIds', type: 'array' },
          { key: 'fundId', type: 'number' },
          { key: 'productName', type: 'string' },
          { key: 'typeName', type: 'string' },
          { key: 'ticketTemplate', type: 'object' }
        ]
      },
      {
        key: 'membershipPerformance',
        type: 'object',
        children: [
          { key: 'performanceDescription', type: 'string' },
          { key: 'levelLabel', type: 'string' }
        ]
      },
      {
        key: 'donation',
        type: 'object',
        children: [
          { key: 'fundId', type: 'number' },
          { key: 'ticketTemplate', type: 'object' }
        ]
      },
      // PACIOLAN
      { key: 'headers', type: 'object' },
      { key: 'policyCode', type: 'string' },
      { key: 'facilityCode', type: 'string' },
      // KISTICKET
      {
        key: 'cognitoCredentials',
        type: 'object',
        children: [
          { key: 'ClientId', type: 'string' },
          { key: 'Password', type: 'string' },
          { key: 'Username', type: 'string' },
          { key: 'UserPoolId', type: 'string' },
          { key: 'IdentityPoolId', type: 'string' },
          { key: 'Region', type: 'string' }
        ]
      },
      { key: 'paymentTypeId', type: 'number' },
      { key: 'cartTimer', type: 'number' },
      { key: 'customerId', type: 'number' },
      { key: 'selectedEventId', type: 'number' },
      { key: 'selectedShowId', type: 'number' },
      { key: 'showVenueInCart', type: 'boolean' },
      // TICKETURE
      { key: 'guestUserEmail', type: 'string' },
      { key: 'channel', type: 'string' },
      { key: 'paymentMethod', type: 'string' },
      { key: 'sellerId', type: 'string' },
      { key: 'donationTicketTypeId', type: 'string' },
      {
        key: 'createUserParams',
        type: 'object',
        children: [
          { key: 'firstName', type: 'string' },
          { key: 'lastName', type: 'string' }
        ]
      },
      {
        key: 'ticketDeliveryTemplates',
        type: 'object',
        children: [
          { key: 'orderTicket', type: 'object' },
          { key: 'dedicatedOrderTicket', type: 'object' },
          { key: 'eventTickets', type: 'array' }
        ]
      },
      { key: 'allowWildcardLimitOnAllMemberTicketTypes', type: 'boolean' },
      // ACME
      { key: 'printerForTicketLayouts', type: 'string' },
      { key: 'donationFundName', type: 'string' },
      {
        key: 'membershipProduct',
        type: 'object',
        children: [
          { key: 'name', type: 'string' },
          { key: 'typeName', type: 'string' }
        ]
      },
      { key: 'ticketLayouts', type: 'array' },
      { key: 'localOnlyPersonTypeIds', type: 'array' },
      // VIVENU
      {
        key: 'createCustomerParams',
        type: 'object',
        children: [
          { key: 'firstName', type: 'string' },
          { key: 'lastName', type: 'string' },
          { key: 'email', type: 'string' },
          { key: 'phone', type: 'string' },
          { key: 'postalCode', type: 'string' },
          { key: 'country', type: 'string' },
          { key: 'createAccount', type: 'boolean' }
        ]
      },
      { key: 'paymentGatewayId', type: 'string' },
      {
        key: 'stripe',
        type: 'object',
        children: [{ key: 'secretKey', type: 'string' }]
      },
      // KIS TECHNOLOGIES
      {
        key: 'paymentSettings',
        type: 'object',
        children: [
          { key: 'isKisMerchantAccount', type: 'boolean' },
          { key: 'gatewayCode', type: 'string' },
          { key: 'gatewayDetails', type: 'object' },
          { key: 'cardProcessingFeeMarkup', type: 'object' },
          { key: 'kisTransactionFees', type: 'array' },
          { key: 'defaultKisProductFee', type: 'object' },
          { key: 'venueTaxes', type: 'array' },
          { key: 'allowRefunds', type: 'boolean' }
        ]
      },
      {
        key: 'sfotSalesReportSettings',
        type: 'object',
        children: [
          { key: 'currentEventStartDate', type: 'string' },
          { key: 'previousEventStartDate', type: 'string' },
          { key: 'kioskMap', type: 'array' }
        ]
      }
    ]
  },
  {
    key: 'cityPass',
    type: 'object',
    children: [
      { key: 'baseUrl', type: 'string' },
      { key: 'username', type: 'string' },
      { key: 'password', type: 'string' },
      { key: 'integrationSettingsList', type: 'array' }
    ]
  }
];

export const kioskColumns = [
  { key: 'name', type: 'string' },
  { key: 'status', type: 'string' },
  { key: 'macAddress', type: 'string' },
  { key: 'setupComplete', type: 'boolean' },
  { key: 'ipAddress', type: 'string' },
  { key: 'unscheduledUntil', type: 'string' },
  { key: 'area', type: 'area' },
  { key: 'configurations', type: 'configuration' },
  { key: 'clientIntegrations', type: 'clientIntegration' },
  {
    key: 'configuration',
    type: 'object',
    children: [
      { key: 'disableHardwarePing', type: 'array' },
      {
        key: 'kioskPreview',
        type: 'object',
        children: [
          { key: 'enabled', type: 'boolean' },
          { key: 'allowCheckOut', type: 'boolean' }
        ]
      },
      { key: 'showInKCC', type: 'boolean' }
    ]
  }
];

export const hardwareColumns = [
  { key: 'name', type: 'string' },
  { key: 'type', type: 'hardwareType' },
  { key: 'code', type: 'string' },
  {
    key: 'details',
    type: 'object',
    children: [
      { key: 'gateway', type: 'string' },
      { key: 'route', type: 'string' }
    ]
  }
];

const messagingConfig = [
  { key: 'reservingTicket', type: 'object' },
  { key: 'ticketsUnavailable', type: 'object' },
  { key: 'eventAPIDown', type: 'object' },
  { key: 'purchaseError', type: 'object' },
  { key: 'globalError', type: 'object' },
  { key: 'ticketServiceDown', type: 'object' },
  { key: 'checkoutCompletion', type: 'object' },
  { key: 'ticketSelectionPage', type: 'object' },
  { key: 'tierPage', type: 'object' },
  { key: 'purchasePaymentPage', type: 'object' },
  { key: 'emailSuggestions', type: 'object' },
  { key: 'eventsPage', type: 'object' },
  { key: 'orderListPage', type: 'object' },
  { key: 'packageTierPage', type: 'object' },
  { key: 'taxesAndFeesLabel', type: 'object' }
];

const infoViewConfig = [
  { key: 'label', type: 'string' },
  { key: 'content', type: 'array' }
];

const baseTicketingConfig = [
  { key: 'priceTypeLimit', type: 'number' },
  { key: 'orderLimit', type: 'number' },
  { key: 'supportName', type: 'object' },
  { key: 'filterByName', type: 'boolean' },
  { key: 'filterByDate', type: 'boolean' },
  {
    key: 'genreSelection',
    type: 'object',
    children: [
      { key: 'enabled', type: 'boolean' },
      { key: 'priorityGenres', type: 'array' },
      { key: 'messages', type: 'object' }
    ]
  },
  { key: 'todayEventFlag', type: 'boolean' },
  { key: 'autoSelectEvent', type: 'boolean' },
  { key: 'allowPaymentCancelation', type: 'boolean' },
  { key: 'cartRules', type: 'array' },
  {
    key: 'customerIdentification',
    type: 'object',
    children: [
      { key: 'lookup', type: 'object' },
      { key: 'creation', type: 'object' }
    ]
  },
  {
    key: 'ticketDeliverySettings',
    type: 'object',
    children: [
      { key: 'deliverySelection', type: 'object' },
      { key: 'email', type: 'object' },
      { key: 'print', type: 'object' },
      { key: 'textMessage', type: 'object' }
    ]
  },
  {
    key: 'eventTierAvailability',
    type: 'object',
    children: [
      { key: 'lowAvailabilityThreshold', type: 'number' },
      { key: 'soldOutThreshold', type: 'number' },
      { key: 'showSoldOut', type: 'boolean' },
      { key: 'messages', type: 'object' }
    ]
  },
  {
    key: 'productAvailabilityAndSelection',
    type: 'object',
    children: [
      { key: 'autoLaunchProduct', type: 'array' },
      { key: 'productsToExcludeFromProductSelection', type: 'array' },
      { key: 'upsell', type: 'object' },
      { key: 'upsellPrompt', type: 'object' }
    ]
  },
  {
    key: 'survey',
    type: 'object',
    children: [
      { key: 'enabled', type: 'boolean' },
      { key: 'surveyList', type: 'array' }
    ]
  },
  { key: 'eventDisplaySettingsList', type: 'array' },
  {
    key: 'donations',
    type: 'object',
    children: [
      { key: 'amounts', type: 'array' },
      { key: 'showInProductSelection', type: 'boolean' },
      { key: 'productName', type: 'object' },
      { key: 'messages', type: 'object' },
      { key: 'giftAid', type: 'object' },
      { key: 'overlay', type: 'object' }
    ]
  },
  {
    key: 'cart',
    type: 'object',
    children: [
      { key: 'enabled', type: 'boolean' },
      { key: 'messages', type: 'object' },
      { key: 'exitOverlayMessages', type: 'object' },
      { key: 'addMoreTickets', type: 'boolean' },
      { key: 'removeTickets', type: 'object' },
      { key: 'seatDetailsOverlayMessage', type: 'object' },
      { key: 'expandItems', type: 'boolean' },
      { key: 'expirationOverlayMessages', type: 'object' },
      { key: 'giftVoucher', type: 'object' },
      { key: 'summary', type: 'object' }
    ]
  },
  { key: 'priorityPhoneCountryCodes', type: 'array' },
  { key: 'priorityPostalCodeLookupCountries', type: 'array' },
  {
    key: 'souvenirTicket',
    type: 'object',
    children: [
      { key: 'enabled', type: 'boolean' },
      { key: 'ticketEntry', type: 'object' }
    ]
  }
];

export const configurationColumns = [
  { key: 'name', type: 'string' },
  {
    key: 'ticketing',
    type: 'object',
    children: [
      ...baseTicketingConfig,
      { key: 'showPriceRange', type: 'boolean' },
      {
        key: 'promoCode',
        type: 'object',
        children: [
          { key: 'priceType', type: 'object' },
          { key: 'cart', type: 'object' },
          { key: 'selection', type: 'object' }
        ]
      },
      {
        key: 'membershipLookup',
        type: 'object',
        children: [
          { key: 'enabled', type: 'boolean' },
          { key: 'messages', type: 'object' },
          { key: 'notFoundMessages', type: 'object' },
          { key: 'scanMembershipCard', type: 'object' },
          { key: 'customSearchOptions', type: 'object' },
          { key: 'tileOrder', type: 'array' },
          { key: 'loadingMessages', type: 'object' }
        ]
      },
      { key: 'membershipSalesSettingsList', type: 'array' },
      {
        key: 'locationValidation',
        type: 'object',
        children: [
          { key: 'enabled', type: 'boolean' },
          { key: 'overlayMessages', type: 'object' },
          { key: 'dataCollection', type: 'object' },
          { key: 'errorOverlayMessages', type: 'object' },
          { key: 'resultCriteria', type: 'object' }
        ]
      }
    ]
  },
  {
    key: 'willCall',
    type: 'object',
    children: [
      { key: 'skipTicketList', type: 'boolean' },
      { key: 'expandedOrderItems', type: 'boolean' },
      { key: 'selectAllTickets', type: 'boolean' },
      { key: 'willCallTileOrder', type: 'array' },
      {
        key: 'scanMembershipCard',
        type: 'object',
        children: [
          { key: 'enabled', type: 'boolean' },
          { key: 'label', type: 'object' },
          { key: 'messages', type: 'object' }
        ]
      },
      {
        key: 'customSearch',
        type: 'object',
        children: [
          { key: 'enabled', type: 'boolean' },
          { key: 'tileOptions', type: 'array' }
        ]
      },
      {
        key: 'errorMessages',
        type: 'object',
        children: [{ key: 'subheader', type: 'object' }]
      },
      {
        key: 'messages',
        type: 'object',
        children: [{ key: 'header', type: 'object' }]
      }
    ]
  },
  {
    key: 'messaging',
    type: 'object',
    children: [
      { key: 'deDE', type: 'object', children: messagingConfig },
      { key: 'enGB', type: 'object', children: messagingConfig },
      { key: 'enUS', type: 'object', children: messagingConfig },
      { key: 'esES', type: 'object', children: messagingConfig },
      { key: 'esMX', type: 'object', children: messagingConfig },
      { key: 'frCA', type: 'object', children: messagingConfig },
      { key: 'frFR', type: 'object', children: messagingConfig },
      { key: 'hiIN', type: 'object', children: messagingConfig },
      { key: 'itIT', type: 'object', children: messagingConfig },
      { key: 'jaJP', type: 'object', children: messagingConfig },
      { key: 'koKR', type: 'object', children: messagingConfig },
      { key: 'plPL', type: 'object', children: messagingConfig },
      { key: 'ptBR', type: 'object', children: messagingConfig },
      { key: 'ptPT', type: 'object', children: messagingConfig },
      { key: 'viVN', type: 'object', children: messagingConfig },
      { key: 'zhCN', type: 'object', children: messagingConfig }
    ]
  },
  {
    key: 'infoView',
    type: 'object',
    children: [
      { key: 'deDE', type: 'object', children: infoViewConfig },
      { key: 'enGB', type: 'object', children: infoViewConfig },
      { key: 'enUS', type: 'object', children: infoViewConfig },
      { key: 'esES', type: 'object', children: infoViewConfig },
      { key: 'esMX', type: 'object', children: infoViewConfig },
      { key: 'frCA', type: 'object', children: infoViewConfig },
      { key: 'frFR', type: 'object', children: infoViewConfig },
      { key: 'hiIN', type: 'object', children: infoViewConfig },
      { key: 'itIT', type: 'object', children: infoViewConfig },
      { key: 'jaJP', type: 'object', children: infoViewConfig },
      { key: 'koKR', type: 'object', children: infoViewConfig },
      { key: 'plPL', type: 'object', children: infoViewConfig },
      { key: 'ptBR', type: 'object', children: infoViewConfig },
      { key: 'ptPT', type: 'object', children: infoViewConfig },
      { key: 'viVN', type: 'object', children: infoViewConfig },
      { key: 'zhCN', type: 'object', children: infoViewConfig }
    ]
  },
  {
    key: 'configuration',
    type: 'object',
    children: [
      {
        key: 'maintenancePage',
        type: 'object',
        children: [{ key: 'enableLastOrderReprint', type: 'boolean' }]
      },
      { key: 'maintenanceLoginTimeout', type: 'number' },
      { key: 'maintenanceTimeout', type: 'number' },
      { key: 'maintenanceTimerCount', type: 'number' },
      { key: 'hideStepper', type: 'boolean' },
      { key: 'languageSettings', type: 'array' },
      { key: 'flowTemplate', type: 'string' },
      {
        key: 'navigationSelection',
        type: 'object',
        children: [
          { key: 'ticketing', type: 'object' },
          { key: 'willCall', type: 'object' },
          { key: 'info', type: 'object' },
          { key: 'cityPass', type: 'object' },
          { key: 'donations', type: 'object' },
          { key: 'tileOrder', type: 'array' },
          { key: 'messages', type: 'object' }
        ]
      },
      {
        key: 'globalSettings',
        type: 'object',
        children: [
          { key: 'fonts', type: 'object' },
          { key: 'palette', type: 'object' },
          { key: 'attractLoop', type: 'object' },
          { key: 'splashPage', type: 'object' },
          { key: 'touchForward', type: 'object' },
          { key: 'inactivityTimeout', type: 'object' }
        ]
      },
      {
        key: 'inactivePage',
        type: 'object',
        children: [
          { key: 'languageTransitionTime', type: 'number' },
          { key: 'messages', type: 'object' }
        ]
      },
      {
        key: 'labels',
        type: 'object',
        children: [
          { key: 'cartItem', type: 'object' },
          { key: 'discount', type: 'object' },
          { key: 'product', type: 'object' },
          { key: 'cart', type: 'object' }
        ]
      }
    ]
  },
  {
    key: 'cityPass',
    type: 'object',
    children: [
      {
        key: 'ticketing',
        type: 'object',
        children: baseTicketingConfig
      },
      {
        key: 'ticketEntry',
        type: 'object',
        children: [
          { key: 'passLimit', type: 'number' },
          { key: 'scanning', type: 'object' },
          { key: 'manualEntry', type: 'object' }
        ]
      },
      { key: 'autoSelectTickets', type: 'boolean' }
    ]
  }
];

export const kioskHardwareColumns = [
  { key: 'hardware', displayName: 'Hardware' },
  { key: 'configuration', displayName: 'configuration' },
  { key: 'values', displayName: 'values' },
  { key: 'serialCode', displayName: 'serialCode' },
  { key: 'status', displayName: 'status' },
  { key: 'action', displayName: 'Action', width: '120px' }
];

export const kioskTicketTemplateColumns = [
  { key: 'ticketTemplate', displayName: 'TicketTemplate' },
  { key: 'action', displayName: 'Action', width: '120px' }
];

export const isValidJSON = (jsonStr: string) => {
  try {
    const obj = JSON.parse(jsonStr);
    if (obj && typeof obj === 'object') {
      return true;
    }
  } catch (err) {
    // DO NOTHING
  }
  return false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getFormattedValue = (item: any) => {
  let formatedVal: string | number = '';
  switch (typeof item) {
    case 'object':
      formatedVal = JSON.stringify(item);
      break;
    case 'number':
      formatedVal = Number(item);
      break;
    case 'boolean':
      formatedVal = JSON.stringify(item);
      break;
    case 'string':
    default:
      formatedVal = item;
      break;
  }

  return formatedVal && formatedVal.toString().length > 80
    ? `${formatedVal.toString().substring(0, 77)}...`
    : formatedVal;
};

export const isValidData = (propertyValue: PropertyType, selectedDataType: string): boolean => {
  if (propertyValue) {
    switch (selectedDataType) {
      case 'object':
        return isValidJSON(propertyValue as string);
      case 'array':
        return isArray(JSON.parse(propertyValue as string));
      case 'number':
        return !isNaN(Number(propertyValue));
      case 'boolean':
        return isBoolean(Boolean(propertyValue));
      case 'string':
      default:
        return true;
    }
  }
  return true;
};

export const getDataTypedValues = (
  selectedDataType: string,
  propertyValue: PropertyType,
  selectedProperty: string,
  selectedColumn: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedColumnValue?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  let updateValues = selectedColumnValue;
  switch (selectedDataType) {
    case 'object':
    case 'array':
      if (selectedColumn === selectedProperty) {
        updateValues = propertyValue ? JSON.parse(propertyValue as string) : null;
      } else {
        updateValues[selectedProperty] = propertyValue ? JSON.parse(propertyValue as string) : null;
      }
      break;
    case 'number':
      if (selectedColumn === selectedProperty) {
        updateValues = Number(propertyValue);
      } else {
        updateValues[selectedProperty] = Number(propertyValue);
      }
      break;
    case 'boolean':
      if (selectedColumn === selectedProperty) {
        updateValues = propertyValue === 'true' || propertyValue === true;
      } else {
        updateValues[selectedProperty] = propertyValue === 'true' || propertyValue === true;
      }
      break;
    case 'string':
    default:
      if (selectedColumn === selectedProperty) {
        updateValues = propertyValue;
      } else {
        updateValues[selectedProperty] = propertyValue;
      }
      break;
  }

  return updateValues;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getUniqueName = (name: string, items: any[]) => {
  let count = 1;
  let uniqueString = '';
  for (let i = 1; i <= count; i++) {
    const occurance = name.indexOf('(');
    if (occurance > 0) {
      uniqueString = `${name.slice(0, occurance)}(${i})`;
    } else {
      uniqueString = `${name}(${i})`;
    }
    // eslint-disable-next-line no-loop-func
    const sameNameItems = items.filter((item) => item.name === uniqueString);
    if (sameNameItems.length > 0) count++;
  }

  return uniqueString;
};

export const formatConfigTitle = (title: string, type?: string) => {
  if (type) {
    if (type === 'configuration') {
      return 'Configuration';
    } else if (type === 'clientIntegration') {
      return 'ClientIntegration';
    } else if (['venue', 'area', 'integration'].includes(type)) {
      return formatTitle(title);
    }
  }

  return title;
};
